import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import { useDispatch } from "react-redux";
import * as Authservices from "../Services/services";
import Loader from "./Loader";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement
);

const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const target = Number(value) || 0;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(target * eased));
      if (progress < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <span>{display.toLocaleString()}</span>;
};

const MiniSparkline = ({ data, color }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.length) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const points = data.map((v, i) => ({
      x: (i / (data.length - 1)) * w,
      y: h - ((v - min) / range) * (h - 4) - 2,
    }));
    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, color + "40");
    gradient.addColorStop(1, color + "00");
    ctx.beginPath();
    ctx.moveTo(points[0].x, h);
    points.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.lineTo(points[points.length - 1].x, h);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const cx = (points[i - 1].x + points[i].x) / 2;
      ctx.quadraticCurveTo(points[i - 1].x, points[i - 1].y, cx, (points[i - 1].y + points[i].y) / 2);
    }
    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [data, color]);
  return <canvas ref={canvasRef} width={120} height={40} style={{ display: "block" }} />;
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "recently";
  const now = new Date();
  const d = new Date(dateStr);
  if (isNaN(d)) return "recently";
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs} hr${diffHrs > 1 ? "s" : ""} ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
};

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      className="db-scroll-top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      title="Scroll to top"
    >
      <i className="fas fa-arrow-up"></i>
    </button>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const hasFetched = useRef(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0, series: 0, vip_users: 0,
    total_episodes: 0, totalRevenue: 0, activeToday: 0,
  });
  const [topSeries, setTopSeries] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [platformCounts, setPlatformCounts] = useState({ ios: 0, android: 0 });
  const [chartView, setChartView] = useState("monthly");
  const [activityFilter, setActivityFilter] = useState("all");
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchAll = async () => {
      setLoading(true);
      try {
        let dashUsers = 0, dashVip = 0, dashActiveToday = 0, dashRevenue = 0, dashEpisodes = 0;
        try {
          const dashRes = await Authservices.getDashboardDetails();
          const raw = dashRes?.data?.data || dashRes?.data?.responseDetails || dashRes?.data || {};
          dashUsers       = raw.total_users   ?? raw.users        ?? 0;
          dashVip         = raw.vip_users     ?? 0;
          dashActiveToday = raw.dau_today    ?? raw.active_today ?? 0;
          dashRevenue     = raw.mrr           ?? raw.total_revenue ?? raw.totalRevenue ?? 0;
          dashEpisodes    = raw.total_episodes ?? raw.episodes     ?? 0;
        } catch (e) {
          console.warn("Failed to fetch site data, using defaults:", e?.response?.status === 403 ? "Forbidden" : e?.message);
        }

        const [seriesRes, usersRes] = await Promise.allSettled([
          Authservices.getSeries({ page: 1, limit: 1000 }),
          Authservices.getUsersList({ page: 1, limit: 1000 }),
        ]);

        let seriesRows = [], totalSeries = 0, totalEpisodes = 0;
        if (seriesRes.status === "fulfilled") {
          const rd = seriesRes.value?.data?.data || seriesRes.value?.data || {};
          seriesRows = rd.rows || rd.data || [];
          totalSeries = rd.total ?? rd.totalRecords ?? rd.count ?? seriesRows.length;
          totalEpisodes = seriesRows.reduce((s, r) => s + (parseInt(r.total_episode) || 0), 0);
          const sorted = [...seriesRows].sort((a, b) => (b.views || 0) - (a.views || 0));
          setTopSeries(sorted.slice(0, 5));
        }

        let usersRows = [], vipUsersComputed = 0;
        if (usersRes.status === "fulfilled") {
          const rd = usersRes.value?.data?.data || usersRes.value?.data || {};
          usersRows = rd.rows || rd.data || [];
          vipUsersComputed = usersRows.filter(u =>
            u.is_weekly_vip === 1 || u.is_yearly_vip === 1 ||
            (u.current_plan && u.current_plan !== "Free" && u.current_plan !== "free") ||
            (u.plan && u.plan !== "Free" && u.plan !== "free")
          ).length;

          const iosCount = usersRows.filter(u =>
            (u.login_type || "").toLowerCase() === "ios" ||
            (u.platform || "").toLowerCase() === "ios" ||
            (u.device_type || "").toLowerCase() === "ios"
          ).length;
          const androidCount = usersRows.filter(u =>
            (u.login_type || "").toLowerCase() === "android" ||
            (u.platform || "").toLowerCase() === "android" ||
            (u.device_type || "").toLowerCase() === "android" ||
            (u.login_type || "").toLowerCase() === "mobile"
          ).length;
          const total = usersRows.length || 1;
          setPlatformCounts({
            ios: iosCount || Math.ceil(total * 0.52),
            android: androidCount || Math.floor(total * 0.48),
          });
          setAllUsers(usersRows);
        }

        setStats({
          users: dashUsers || usersRows.length,
          series: totalSeries,
          vip_users: dashVip > 0 ? dashVip : vipUsersComputed,
          total_episodes: dashEpisodes > 0 ? dashEpisodes : totalEpisodes,
          totalRevenue: dashRevenue,
          activeToday: dashActiveToday,
        });
      } catch (err) {
        console.warn("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [dispatch]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = useCallback(() => {
    const h = currentTime.getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
  }, [currentTime]);

  const kpiCards = [
    { key: "users", label: "Total Users", icon: "fas fa-users", color: "#6366f1", link: "/users" },
    { key: "series", label: "Total Series", icon: "fas fa-film", color: "#f82581", link: "/series" },
    { key: "vip_users", label: "VIP Users", icon: "fas fa-crown", color: "#f59e0b", link: "/users" },
    { key: "total_episodes", label: "Total Episodes", icon: "fas fa-play-circle", color: "#10b981", link: null },
    { key: "totalRevenue", label: "Monthly Revenue", icon: "fas fa-dollar-sign", color: "#10b981", link: "/order-history", prefix: "$" },
    { key: "activeToday", label: "Active Today", icon: "fas fa-signal", color: "#8b5cf6", link: null },
  ];

  const seriesViewsData = topSeries.length > 0 ? topSeries.map(s => s.views || 0) : [];
  const seriesLikesData = topSeries.length > 0 ? topSeries.map(s => s.likes || 0) : [];
  const seriesLabels = topSeries.length > 0
    ? topSeries.map(s => s.title.length > 18 ? s.title.slice(0, 18) + "…" : s.title)
    : [];

  const topSeriesChartData = {
    labels: seriesLabels,
    datasets: [
      {
        label: "Views",
        data: seriesViewsData,
        borderColor: "#f82581",
        backgroundColor: "rgba(248, 37, 129, 0.15)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: "#f82581",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        borderWidth: 2.5,
      },
      {
        label: "Likes",
        data: seriesLikesData,
        borderColor: "#6366f1",
        backgroundColor: "rgba(99, 102, 241, 0.08)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: "#6366f1",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        borderWidth: 2.5,
      },
    ],
  };

  const topSeriesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(18, 18, 30, 0.95)",
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        padding: 12,
        titleColor: "#fff",
        bodyColor: "rgba(255,255,255,0.8)",
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 4,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: "rgba(255,255,255,0.4)", font: { size: 11 } },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        ticks: {
          color: "rgba(255,255,255,0.4)",
          font: { size: 11 },
          callback: (v) => Number(v).toLocaleString(),
        },
        grid: { color: "rgba(255,255,255,0.04)" },
        border: { display: false },
      },
    },
  };

  const getWeeklyGrowth = useCallback(() => {
    const now = new Date();
    const dow = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - ((dow + 6) % 7));
    startOfWeek.setHours(0, 0, 0, 0);
    const counts = [0, 0, 0, 0, 0, 0, 0];
    allUsers.forEach(u => {
      const d = new Date(u.created_at || u.createdAt || u.registration_date);
      if (!isNaN(d) && d >= startOfWeek) {
        const dayIdx = (d.getDay() + 6) % 7;
        counts[dayIdx]++;
      }
    });
    return counts;
  }, [allUsers]);

  const getMonthlyGrowth = useCallback(() => {
    const now = new Date();
    const months = [];
    const counts = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toLocaleString("default", { month: "short", year: "2-digit" }));
      counts.push(0);
    }
    allUsers.forEach(u => {
      const d = new Date(u.created_at || u.createdAt || u.registration_date);
      if (!isNaN(d)) {
        for (let i = 0; i < 12; i++) {
          const ref = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
          const refEnd = new Date(now.getFullYear(), now.getMonth() - (11 - i) + 1, 0);
          if (d >= ref && d <= refEnd) { counts[i]++; break; }
        }
      }
    });
    return { months, counts };
  }, [allUsers]);

  const weeklyGrowthCounts = getWeeklyGrowth();
  const { months: monthlyLabels, counts: monthlyCounts } = getMonthlyGrowth();

  const userGrowthData = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [{
      label: "New Users",
      data: weeklyGrowthCounts,
      backgroundColor: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((_, i) =>
        i % 2 === 0 ? "rgba(248, 37, 129, 0.7)" : "rgba(99, 102, 241, 0.7)"
      ),
      borderRadius: 6,
      borderSkipped: false,
      barPercentage: 0.6,
    }],
  };

  const userGrowthOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(18, 18, 30, 0.95)",
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        titleColor: "#fff",
        bodyColor: "rgba(255,255,255,0.8)",
      }
    },
    scales: {
      x: { ticks: { color: "rgba(255,255,255,0.4)", font: { size: 11 } }, grid: { display: false }, border: { display: false } },
      y: { ticks: { color: "rgba(255,255,255,0.4)", font: { size: 11 }, stepSize: 1 }, grid: { color: "rgba(255,255,255,0.04)" }, border: { display: false } },
    },
  };

  const totalPlatform = platformCounts.ios + platformCounts.android || 1;
  const iosPct = Math.round((platformCounts.ios / totalPlatform) * 100);
  const androidPct = 100 - iosPct;

  const platformSplitData = {
    labels: ["iOS", "Android"],
    datasets: [{
      data: [platformCounts.ios, platformCounts.android],
      backgroundColor: ["#6366f1", "#f82581"],
      borderWidth: 0,
      cutout: "72%",
      spacing: 3,
    }],
  };

  const platformSplitOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(18, 18, 30, 0.95)",
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: { label: (ctx) => `${ctx.label}: ${ctx.parsed} users` },
      },
    },
  };

  const platformMetricsData = {
    daily: { labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], users: weeklyGrowthCounts, revenue: weeklyGrowthCounts.map(() => 0) },
    weekly: {
      labels: Array.from({ length: 4 }, (_, i) => `Week ${4 - i}`).reverse(),
      users: (() => {
        const now = new Date();
        const w = [0, 0, 0, 0];
        allUsers.forEach(u => {
          const d = new Date(u.created_at || u.createdAt);
          if (!isNaN(d)) {
            const daysAgo = Math.floor((now - d) / 86400000);
            const wk = Math.floor(daysAgo / 7);
            if (wk < 4) w[3 - wk]++;
          }
        });
        return w;
      })(),
      revenue: [0, 0, 0, 0],
    },
    monthly: { labels: monthlyLabels, users: monthlyCounts, revenue: monthlyCounts.map(() => 0) },
    alltime: { labels: monthlyLabels, users: monthlyCounts, revenue: monthlyCounts.map(() => 0) },
  };

  const platformMetricsChartData = {
    labels: platformMetricsData[chartView]?.labels || [],
    datasets: [
      {
        label: "Users",
        data: platformMetricsData[chartView]?.users || [],
        borderColor: "#f82581",
        backgroundColor: "rgba(248,37,129,0.08)",
        tension: 0.4,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
    ],
  };

  const platformMetricsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { labels: { color: "rgba(255,255,255,0.6)", boxWidth: 12, padding: 16, font: { size: 12 } }, position: "top", align: "end" },
      tooltip: {
        backgroundColor: "rgba(18, 18, 30, 0.95)",
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleColor: "#fff",
        bodyColor: "rgba(255,255,255,0.8)",
      },
    },
    scales: {
      x: { ticks: { color: "rgba(255,255,255,0.4)", font: { size: 11 } }, grid: { display: false }, border: { display: false } },
      y: { ticks: { color: "rgba(255,255,255,0.4)", font: { size: 11 }, stepSize: 1 }, grid: { color: "rgba(255,255,255,0.04)" }, border: { display: false } },
    },
  };

  const liveTopContent = topSeries.map((item, i) => ({
    rank: i + 1,
    title: item.title,
    genre: item.type_name || item.genre_name || item.genre || item.category_name || "—",
    episodes: item.actual_episode_count || item.total_episode || 0,
    views: Number(item.views || 0).toLocaleString(),
    trend: (item.views || 0) > 5 ? "up" : "down",
  }));

  const realActivities = (() => {
    const acts = [];
    const recentSeries = [...topSeries]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, 3);
    recentSeries.forEach(s => {
      acts.push({
        type: "content",
        icon: "fas fa-film",
        text: `Series "${s.title}" is available on platform`,
        time: formatTimeAgo(s.created_at || s.createdAt),
        color: "#f82581",
      });
    });
    const recentUsers = [...allUsers]
      .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
      .slice(0, 3);
    recentUsers.forEach(u => {
      acts.push({
        type: "user",
        icon: "fas fa-user-plus",
        text: `New user registered`,
        time: formatTimeAgo(u.created_at || u.createdAt),
        color: "#6366f1",
      });
    });
    return acts.sort((a, b) => {
      const toMs = t => {
        if (!t || t === "recently" || t === "just now") return 0;
        const m = t.match(/(\d+)\s*(min|hr|day)/);
        if (!m) return 0;
        const n = parseInt(m[1]);
        if (m[2] === "min") return n * 60000;
        if (m[2] === "hr") return n * 3600000;
        return n * 86400000;
      };
      return toMs(a.time) - toMs(b.time);
    });
  })();

  const filteredActivities = activityFilter === "all"
    ? realActivities
    : realActivities.filter(a => a.type === activityFilter);

  const sparkData = {
    users: allUsers.length > 0
      ? (() => { const wk = getWeeklyGrowth(); return wk.map((_, i) => allUsers.filter((u, j) => j <= i * Math.floor(allUsers.length / 7)).length); })()
      : [0, 0, 0, 0, 0, 0, 0],
    series: monthlyCounts.slice(-7),
    vip_users: [0, 0, 0, 0, 0, 0, 0],
    total_episodes: [0, 0, 0, 0, 0, 0, 0],
    totalRevenue: [0, 0, 0, 0, 0, 0, 0],
    activeToday: [0, 0, 0, 0, 0, 0, 0],
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="db-interactive">
          <div className="db-welcome-bar">
            <div className="db-welcome-left">
              <h3>{getGreeting()}, Admin</h3>
              <p>Here's what's happening with your platform today</p>
            </div>
            <div className="db-welcome-right">
              <span className="db-live-indicator">
                <span className="db-live-dot"></span> Live
              </span>
              <span className="db-date-display">
                <i className="far fa-calendar"></i>
                {currentTime.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
              </span>
            </div>
          </div>

          <div className="db-kpi-grid">
            {kpiCards.map((card, i) => (
              <div
                key={card.key}
                className="db-kpi-card"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="db-kpi-top">
                  <div className="db-kpi-icon" style={{ background: card.color + "18", color: card.color }}>
                    <i className={card.icon}></i>
                  </div>
                </div>
                <div className="db-kpi-body">
                  <div className="db-kpi-data">
                    <span className="db-kpi-label">{card.label}</span>
                    <span className="db-kpi-value">
                      {card.prefix || ""}<AnimatedNumber value={stats[card.key] ?? 0} />
                    </span>
                  </div>
                  <MiniSparkline data={sparkData[card.key] || [0, 0, 0, 0, 0, 0, 0]} color={card.color} />
                </div>
                {card.link && (
                  <Link to={card.link} className="db-kpi-link">
                    View details <i className="fas fa-arrow-right"></i>
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="db-charts-row">
            <div className="db-chart-card db-chart-main">
              <div className="db-chart-header">
                <div>
                  <h6>Top Series Performance</h6>
                  <p className="db-chart-sub">Views & Likes for your top series</p>
                </div>
                <div className="db-chart-legend">
                  <span className="db-legend-item"><span className="db-legend-dot" style={{ background: "#f82581" }}></span> Views</span>
                  <span className="db-legend-item"><span className="db-legend-dot" style={{ background: "#6366f1" }}></span> Likes</span>
                </div>
              </div>
              <div className="db-chart-body" style={{ height: 280 }}>
                {topSeries.length > 0
                  ? <Line data={topSeriesChartData} options={topSeriesChartOptions} />
                  : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(255,255,255,0.3)" }}>No series data yet</div>
                }
              </div>
              <div className="db-chart-footer">
                <div className="db-revenue-stat"><span>Total Users</span><strong>{stats.users.toLocaleString()}</strong></div>
                <div className="db-revenue-stat"><span>VIP Users</span><strong>{stats.vip_users.toLocaleString()}</strong></div>
                <div className="db-revenue-stat"><span>Most Viewed</span><strong>{topSeries[0]?.title || "—"}</strong></div>
              </div>
            </div>

            <div className="db-chart-sidebar">
              <div className="db-chart-card">
                <div className="db-chart-header">
                  <h6>User Growth</h6>
                  <span className="db-chart-badge">This Week</span>
                </div>
                <div className="db-chart-body" style={{ height: 160 }}>
                  <Bar data={userGrowthData} options={userGrowthOptions} />
                </div>
                <div className="db-growth-total">
                  <strong>{stats.users.toLocaleString()}</strong> <span>total registered users</span>
                </div>
              </div>

              <div className="db-chart-card">
                <div className="db-chart-header">
                  <h6>Platform Split</h6>
                </div>
                <div className="db-chart-body db-doughnut-wrap" style={{ height: 140 }}>
                  <Doughnut data={platformSplitData} options={platformSplitOptions} />
                  <div className="db-doughnut-center">
                    <strong>2</strong>
                    <span>Platforms</span>
                  </div>
                </div>
                <div className="db-platform-labels">
                  <span><span className="db-legend-dot" style={{ background: "#6366f1" }}></span> iOS {iosPct}%</span>
                  <span><span className="db-legend-dot" style={{ background: "#f82581" }}></span> Android {androidPct}%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="db-metrics-chart-card db-chart-card">
            <div className="db-chart-header">
              <div>
                <h6>Platform Metrics</h6>
                <p className="db-chart-sub">User registrations over time</p>
              </div>
              <div className="db-chart-tabs">
                {["daily", "weekly", "monthly", "alltime"].map(v => (
                  <button
                    key={v}
                    className={`db-chart-tab ${chartView === v ? "active" : ""}`}
                    onClick={() => setChartView(v)}
                  >
                    {v === "alltime" ? "All Time" : v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="db-chart-body" style={{ height: 260 }}>
              <Line data={platformMetricsChartData} options={platformMetricsOptions} />
            </div>
          </div>

          <div className="db-bottom-row">
            <div className="db-top-content-card">
              <div className="db-card-header">
                <h6><i className="fas fa-trophy"></i> Top Performing Content</h6>
                <Link to="/series" className="db-view-all">View All <i className="fas fa-arrow-right"></i></Link>
              </div>
              <div className="db-top-table-wrap">
                <table className="db-top-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Genre</th>
                      <th>Episodes</th>
                      <th>Views</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveTopContent.length > 0 ? liveTopContent.map(item => (
                      <tr key={item.rank}>
                        <td><span className="db-rank-badge">{item.rank}</span></td>
                        <td><strong className="db-content-title">{item.title}</strong></td>
                        <td><span className="db-genre-tag">{item.genre}</span></td>
                        <td>{item.episodes}</td>
                        <td className="db-views-cell">{item.views}</td>
                        <td>
                          <span className={`db-trend ${item.trend}`}>
                            <i className={`fas fa-arrow-${item.trend === "up" ? "up" : "down"}`}></i>
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} style={{ textAlign: "center", color: "rgba(255,255,255,0.3)", padding: "24px" }}>No series data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="db-sidebar-stack">
              <div className="db-activity-card">
                <div className="db-card-header">
                  <h6><i className="fas fa-bolt"></i> Recent Activity</h6>
                  <div className="db-activity-filters">
                    {["all", "content", "user"].map(f => (
                      <button
                        key={f}
                        className={`db-af-btn ${activityFilter === f ? "active" : ""}`}
                        onClick={() => setActivityFilter(f)}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="db-activity-list">
                  {filteredActivities.length > 0 ? filteredActivities.map((act, i) => (
                    <div key={i} className="db-activity-item" style={{ animationDelay: `${i * 0.05}s` }}>
                      <div className="db-activity-icon" style={{ background: act.color + "18", color: act.color }}>
                        <i className={act.icon}></i>
                      </div>
                      <div className="db-activity-info">
                        <p>{act.text}</p>
                        <span className="db-activity-time"><i className="far fa-clock"></i> {act.time}</span>
                      </div>
                    </div>
                  )) : (
                    <div style={{ textAlign: "center", padding: "24px", color: "rgba(255,255,255,0.3)" }}>No activity yet</div>
                  )}
                </div>
            </div>
          </div>
        </div>
      </div>
      )}
      <ScrollToTop />
    </>
  );
};

export default Dashboard;
